import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useBranding } from '@/lib/useBranding'
import { errorMessage } from '@/api/client'
import { orderLinkApi } from '@/api/endpoints'
import { waLink } from '@/lib/whatsapp'

/**
 * The page a customer opens from the link we send them on WhatsApp.
 *
 * No login: the token in the URL is the whole credential, and it reaches one
 * order. Everything the WhatsApp conversation used to do one question at a
 * time happens here at once — the lines are all visible, the ones we guessed
 * at are marked, and a product is chosen from a search box rather than typed
 * and re-read.
 *
 * Built for a phone held in one hand at a building site, not for a desk: one
 * column, large touch targets, and totals that stay visible while the lines
 * are edited.
 *
 * Three steps, one page — review, code, done. A customer who gets a code and
 * then wants to change one more quantity can go back without losing the
 * order, which a separate route would make awkward.
 *
 * Every edit goes to the server and the server's answer replaces what is on
 * screen. Nothing is priced here: a bag is 25kg or 20kg depending on the
 * category, tax is carved out or added on depending on the SKU, and a page
 * that guessed at any of that would show a total the invoice then disagrees
 * with.
 */

const OTP_LENGTH = 6

/**
 * The order's GST by rate: [{ rate: '18%', amount }]. One unlabelled row of
 * the order's own total when no line carries a rate.
 */
const gstByRate = (order) => {
  const byRate = new Map()
  for (const line of order.lines) {
    if (line.tax_rate == null) continue
    const rate = Number(line.tax_rate)
    byRate.set(rate, (byRate.get(rate) ?? 0) + Number(line.line_tax ?? 0))
  }
  if (!byRate.size) return [{ rate: '', amount: order.tax_total }]
  return [...byRate.entries()]
    .sort(([a], [b]) => a - b)
    .map(([rate, amount]) => ({ rate: `${rate}%`, amount }))
}

const money = (n) =>
  `₹${Number(n ?? 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const unitLabel = (unit) => (unit ?? '').toLowerCase()

// 'KILOGRAMS' -> 'kg'. Only for the units whose full name is longer than the
// number beside it: '7 kilograms · ₹105.00' is mostly the word 'kilograms'.
const SHORT_UNITS = { KILOGRAMS: 'kg', GRAMS: 'g', METERS: 'm' }

/**
 * A quantity's unit, short where there is a short form and singular where
 * the quantity is one — the catalog stores 'CARTONS', so without this a
 * single one reads '1 cartons'.
 */
const qtyUnit = (quantity, unit) => {
  const short = SHORT_UNITS[(unit ?? '').toUpperCase()]
  if (short) return short
  const name = (unit ?? '').toLowerCase()
  return Number(quantity) === 1 ? name.replace(/s$/, '') : name
}

// Multiples of a product's minimum, to three decimals as quantities are kept.
const isMultiple = (value, step) => {
  const ratio = Number(value) / Number(step)
  return Math.abs(ratio - Math.round(ratio)) < 1e-6
}
const roundUp = (value, step) =>
  Number((Math.max(1, Math.ceil(Number(value) / Number(step) - 1e-9)) * Number(step)).toFixed(3))
// '50', '2.5' — no trailing zeros.
const qty = (value) => String(Number(Number(value).toFixed(3)))

/** A unit option as both the dropdown and the single-unit text show it. */
const unitName = (option) =>
  option ? (option.label_ta ? `${option.label} / ${option.label_ta}` : option.label) : null

/**
 * A server timestamp, in the reader's own clock.
 *
 * The 'Z' matters. The API sends a naive timestamp — '2026-09-30T07:52:10' —
 * and it is UTC, but JavaScript reads one without a zone as *local* time. On
 * a phone in Tamil Nadu that turns 13:22 into 07:52, and the customer is
 * shown an order placed five and a half hours before they sent the message.
 */
const whenCreated = (iso) => {
  if (!iso) return null
  const text = String(iso)
  const stamped = /(Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}Z`
  const at = new Date(stamped)
  if (Number.isNaN(at.getTime())) return null
  return at.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function CustomerOrder() {
  const { token } = useParams()

  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dead, setDead] = useState(null)      // the link no longer works
  const [busy, setBusy] = useState(false)     // an edit is in flight
  const [problem, setProblem] = useState('')
  // review | catalog | otp | done | delivered
  const [step, setStep] = useState('review')

  // The confirmed order is kept aside: confirming spends the link, so a
  // reload after it cannot fetch anything, and the done screen has to hold
  // what it was told rather than ask again.
  const [receipt, setReceipt] = useState(null)

  useEffect(() => {
    let cancelled = false
    orderLinkApi
      .get(token)
      .then((data) => {
        if (cancelled) return
        setOrder(data)
        if (data.delivered || data.on_the_way) {
          // Delivered, or dispatched and on its way: the link was sent so
          // they can confirm it arrived. On a dispatched order the same
          // button marks it delivered too.
          setStep('delivered')
        } else if (data.placed) {
          setReceipt(data)
          setStep('done')
        } else if (data.awaiting_code) {
          // They asked for a code and then reloaded; put them back where
          // they were rather than making them start again.
          setStep('otp')
        }
      })
      .catch((error) => {
        if (!cancelled) setDead(errorMessage(error))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  /**
   * Run an edit, and take the re-priced order back as the truth. Resolves
   * true when it went through, so a form can close only on success.
   */
  const edit = useCallback(async (run) => {
    setBusy(true)
    setProblem('')
    try {
      setOrder(await run())
      return true
    } catch (error) {
      // A 404 mid-session means the link died under them — expired, or
      // confirmed from another tab. Anything else is worth showing and
      // recovering from.
      if (error?.response?.status === 404) setDead(errorMessage(error))
      else setProblem(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }, [])

  if (loading) {
    return (
      <Shell>
        <p className="co-empty">Loading your order…</p>
      </Shell>
    )
  }

  if (dead) {
    return (
      <Shell>
        <div className="co-done">
          <h1>This link has expired</h1>
          <p className="co-sub">{dead}</p>
          <p className="co-sub">
            Send us a message on WhatsApp and we will send you a new one.
          </p>
          {/* The message is written for them. A customer who has just been
              told their link is dead should not also have to work out what
              to say, and a reply that names the problem is one the shop can
              act on without asking anything back.

              Absent when no number is configured, rather than a button that
              goes nowhere: the sentence above still tells them what to do. */}
          {waLink() && (
            <p>
              <a
                className="co-wa"
                href={waLink(
                  'Hi, my order link has expired. Please send me a new one.',
                )}
                target="_blank"
                rel="noreferrer"
              >
                Message us on WhatsApp
              </a>
            </p>
          )}
        </div>
      </Shell>
    )
  }

  if (step === 'delivered') {
    return <Delivered token={token} order={order} onOrder={setOrder} onDead={setDead} />
  }

  if (step === 'done' && receipt) {
    return <Done orderNumber={receipt.order_number} total={receipt.grand_total} />
  }

  if (step === 'catalog') {
    return (
      <Catalog
        token={token}
        order={order}
        problem={problem}
        edit={edit}
        onBack={() => setStep('review')}
      />
    )
  }

  if (step === 'otp') {
    return (
      <OtpStep
        token={token}
        phone={order.customer_phone}
        total={order.grand_total}
        onBack={() => setStep('review')}
        onVerified={(confirmed) => {
          setReceipt(confirmed)
          setStep('done')
        }}
        onDead={setDead}
      />
    )
  }

  return (
    <Review
      token={token}
      order={order}
      busy={busy}
      problem={problem}
      edit={edit}
      onBrowse={() => {
        setProblem('')
        setStep('catalog')
      }}
      onConfirm={async () => {
        setBusy(true)
        setProblem('')
        try {
          setOrder(await orderLinkApi.requestCode(token))
          setStep('otp')
        } catch (error) {
          if (error?.response?.status === 404) setDead(errorMessage(error))
          else setProblem(errorMessage(error))
        } finally {
          setBusy(false)
        }
      }}
    />
  )
}

/**
 * Everything the shop sells, on one page.
 *
 * One page and one scroll, deliberately. The first version of this made the
 * customer pick a category, read it, come back and pick another — so nothing
 * was visible until they had committed to a guess, and the way back meant two
 * different things depending on how deep they were. Here the whole list is
 * simply there, under headings, the way a price list on paper is.
 *
 * Every row says the same three things in the same places: what it is, what
 * it costs, and whether it is on the order. Tapping adds one. The page stays
 * put afterwards, because somebody reading a list is rarely after one thing.
 */
function Catalog({ token, order, problem, edit, onBack }) {
  const [items, setItems] = useState(null)   // null until the list arrives

  useEffect(() => {
    let cancelled = false
    orderLinkApi
      .productsIn(token, null)
      .then((rows) => {
        if (!cancelled) setItems(rows)
      })
      .catch(() => {
        if (!cancelled) setItems([])
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')   // '' is every category

  // Built from the products themselves rather than asked for separately, so
  // the row can never offer a category that turns out to hold nothing.
  const categories = useMemo(() => {
    const counts = new Map()
    for (const product of items ?? []) {
      const name = product.category || 'Other'
      counts.set(name, (counts.get(name) ?? 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [items])

  // Under headings, in the order the shop names them. The server sorts
  // products by name, so each heading's rows are already in order.
  //
  // Both narrowings happen here and not at the server: the whole catalog is
  // already in hand, so a customer types and sees the list shrink with no
  // request, no delay and nothing to debounce on a phone at a building site.
  const groups = useMemo(() => {
    const term = query.trim().toLowerCase()
    const byCategory = new Map()
    for (const product of items ?? []) {
      const name = product.category || 'Other'
      if (category && name !== category) continue
      if (term && !productMatches(product, term)) continue
      if (!byCategory.has(name)) byCategory.set(name, [])
      byCategory.get(name).push(product)
    }
    return [...byCategory.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [items, query, category])

  const shown = groups.reduce((total, [, products]) => total + products.length, 0)

  // What the order says about each product, worked out once per render
  // rather than scanning the lines again for every one of fifty-odd rows.
  //
  // `quantity` adds up every line of the product, but `line` is the newest
  // of them — the one a change lands on. The stepper and the unit picker
  // both use it, so they can never disagree about which line they are
  // editing. A message that named the same product twice keeps both lines,
  // and the order screen still shows them separately.
  const byProduct = useMemo(() => {
    const map = new Map()
    for (const line of order.lines) {
      if (line.product_id == null) continue
      const seen = map.get(line.product_id)
      map.set(line.product_id, {
        quantity: (seen?.quantity ?? 0) + Number(line.quantity || 0),
        line,
      })
    }
    return map
  }, [order.lines])

  // Which row is waiting on the server. Only that row's controls are
  // disabled: `busy` covers the whole order, and using it here greyed out
  // all fifty rows every time one number changed.
  const [pending, setPending] = useState(null)

  const run = useCallback(
    async (productId, action) => {
      setPending(productId)
      try {
        await edit(action)
      } finally {
        setPending(null)
      }
    },
    [edit],
  )

  /**
   * One more or one fewer of this product.
   *
   * Down to nothing removes the line rather than storing a zero: the server
   * refuses a quantity of zero, and an order carrying a line of none of
   * something is not what the customer means by taking it off.
   */
  const onStep = useCallback(
    (product, line, delta) => {
      if (!line) {
        if (delta > 0) {
          run(product.id, () =>
            orderLinkApi.addLine(token, {
              product_id: product.id,
              quantity: startingQuantity(product),
              unit: product.unit,
            }),
          )
        }
        return
      }
      // In steps of the minimum — 25, 50, 75 kg — where the product has one.
      const by = Number(line.quantity_step) || 1
      const next = Number((Number(line.quantity) + delta * by).toFixed(3))
      run(product.id, () =>
        next <= 0
          ? orderLinkApi.removeLine(token, line.id)
          : orderLinkApi.updateLine(token, line.id, { quantity: String(next) }),
      )
    },
    [run, token],
  )

  const onUnit = useCallback(
    (product, line, unit) =>
      run(product.id, () => orderLinkApi.updateLine(token, line.id, { unit })),
    [run, token],
  )

  return (
    <Shell
      /* On the logo's row, the same place the way in sat on the order
         screen. The bar at the foot says the same thing, because the list
         is long enough that this one scrolls away. */
      action={
        <button type="button" className="co-browse" onClick={onBack}>
          Back to order
        </button>
      }
    >
      <header className="co-head">
        <div className="co-head-text">
          <h1>Our products</h1>
          <p className="co-sub">
            Add products and adjust quantities as needed.
          </p>
        </div>
      </header>

      {problem && <p className="co-error">{problem}</p>}

      {items === null && <p className="co-empty">Loading…</p>}
      {items?.length === 0 && (
        <p className="co-empty">Our list is empty just now.</p>
      )}

      {items?.length > 0 && (
        <div className="co-filter">
          <input
            type="search"
            className="co-search"
            placeholder="Search products"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search products"
          />
          {/* One row that scrolls sideways rather than a dropdown: the
              categories are the shop's own words and worth reading, and
              nine of them wrap to three lines on a phone. */}
          <div className="co-chips">
            <button
              type="button"
              className={`co-chip${category === '' ? ' co-chip-on' : ''}`}
              onClick={() => setCategory('')}
            >
              All <span className="co-chip-n">{items.length}</span>
            </button>
            {categories.map(([name, count]) => (
              <button
                type="button"
                key={name}
                className={`co-chip${category === name ? ' co-chip-on' : ''}`}
                onClick={() => setCategory(category === name ? '' : name)}
              >
                {name} <span className="co-chip-n">{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {items?.length > 0 && shown === 0 && (
        <p className="co-empty">
          No matching items found.{' '}
          <button
            type="button"
            className="co-link co-clear"
            onClick={() => {
              setQuery('')
              setCategory('')
            }}
          >
            Show all
          </button>
        </p>
      )}

      {groups.map(([category, products]) => (
        <section className="co-card co-group" key={category}>
          {/* Sticks to the top while its own rows scroll past, so the answer
              to "what am I looking at" is always on screen. */}
          <h2 className="co-group-head">{category}</h2>

          {products.map((product) => {
            const on = byProduct.get(product.id)
            return (
              <ProductRow
                key={product.id}
                product={product}
                quantity={on?.quantity ?? 0}
                line={on?.line}
                pending={pending === product.id}
                onStep={onStep}
                onUnit={onUnit}
              />
            )
          })}
        </section>
      ))}

      {/* The same shape as the bar on the order screen, so the way out of
          this page is where the way on was. */}
      <div className="co-bar">
        <div>
          <div className="co-muted">
            {order.lines.length} {order.lines.length === 1 ? 'product' : 'products'}
          </div>
          <div className="co-bar-total">{money(order.grand_total)}</div>
        </div>
        <button type="button" className="co-primary" onClick={onBack}>
          Back to my order
        </button>
      </div>
    </Shell>
  )
}

/**
 * Does this product answer what was typed?
 *
 * Name, Tamil name, code and category all count. A customer looking for
 * binding wire types 'binding wire', which is the category — the SKU itself
 * is 'MS WIRE IN COIL 18SWG', so matching names alone finds nothing.
 */
const productMatches = (product, term) =>
  [product.name, product.name_ta, product.code, product.category].some(
    (field) => (field ?? '').toLowerCase().includes(term),
  )

/** The asterisk on a required field's label. */
/** 'A, B and C' — the products in a list of lines, for a message about them. */
const names = (lines) => {
  const all = lines.map((l) => l.product_name).filter(Boolean)
  if (all.length < 2) return all.join('')
  return `${all.slice(0, -1).join(', ')} and ${all[all.length - 1]}`
}

const Req = () => (
  <span className="co-req" aria-hidden="true">
    *
  </span>
)

/**
 * What is wrong with one field, under that field.
 *
 * `role="alert"` so a screen reader says it when it appears, rather than
 * only when the field is next focused.
 */
const FieldError = ({ message }) =>
  message ? (
    <span className="co-field-error" role="alert">
      {message}
    </span>
  ) : null

/** Two unit lists that would draw the same dropdown. */
const sameUnits = (a, b) =>
  a === b ||
  (!!a &&
    !!b &&
    a.length === b.length &&
    a.every((o, i) => o.code === b[i].code && o.is_base === b[i].is_base))

/**
 * One product in the list.
 *
 * Memoised, and the comparison is written out rather than left to a shallow
 * one, because every edit replaces the whole order: each line comes back a
 * new object with the same values in it, so a shallow compare would call
 * every row on the order changed and redraw the lot. Comparing what the row
 * actually draws means a change to one product redraws one product.
 */
const ProductRow = memo(
  function ProductRow({ product, quantity, line, pending, onStep, onUnit }) {
    return (
      <div className={`co-prod${quantity > 0 ? ' co-prod-on' : ''}`}>
        <div className="co-prod-text">
          <span className="co-line-name">{product.name}</span>
          {product.name_ta && (
            <span className="co-name-ta">{product.name_ta}</span>
          )}
          {/* Under the name, not opposite it: the price is part of reading
              the product, and the only thing opposite is what you can do. */}
          {line?.below_minimum && (
            <span className="co-needs-qty">Minimum order is {line.minimum}.</span>
          )}
          <span className="co-prod-price">
            {product.rate == null
              ? 'Price on request'
              : `${money(product.rate)} / ${
                  product.unit === 'KILOGRAMS' ? 'kg' : unitLabel(product.unit)
                }`}
          </span>
        </div>

        {/* Nothing on the order yet: one button, one word. Once it is on, the
            same corner becomes the count and the ways to change it, so the
            customer never leaves this page to fix an amount they got wrong. */}
        {quantity > 0 ? (
          <div className="co-qty">
            {/* Only the units this product converts to — the same list the
                order screen offers, read off the line itself so the two
                screens cannot disagree. One that will not convert would
                price the line at zero. */}
            {line?.unit_options?.length > 1 ? (
              <select
                className="co-unit-select"
                value={line.unit}
                disabled={pending}
                aria-label={`Unit for ${product.name}`}
                onChange={(e) => onUnit(product, line, e.target.value)}
              >
                {line.unit_options.map((option) => (
                  <option key={option.code} value={option.code}>
                    {unitName(option)}
                    {option.is_base ? ' ·' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <span className="co-unit">
                {unitName(line?.unit_options?.find((o) => o.code === line.unit)) ??
                  unitLabel(line?.unit ?? product.unit)}
              </span>
            )}
            <button
              type="button"
              className="co-qty-btn"
              disabled={pending}
              aria-label={`One less ${product.name}`}
              onClick={() => onStep(product, line, -1)}
            >
              −
            </button>
            <span className="co-qty-n">{quantity}</span>
            <button
              type="button"
              className="co-qty-btn"
              disabled={pending}
              aria-label={`One more ${product.name}`}
              onClick={() => onStep(product, line, 1)}
            >
              +
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="co-pill"
            disabled={pending}
            onClick={() => onStep(product, undefined, 1)}
          >
            Add
          </button>
        )}
      </div>
    )
  },
  (before, after) =>
    before.product === after.product &&
    before.quantity === after.quantity &&
    before.pending === after.pending &&
    before.line?.id === after.line?.id &&
    before.line?.unit === after.line?.unit &&
    sameUnits(before.line?.unit_options, after.line?.unit_options) &&
    before.onStep === after.onStep &&
    before.onUnit === after.onUnit,
)

const STEP_NAMES = ['Products', 'Delivery', 'Transport', 'Review']

/**
 * Which of the four the customer is on.
 *
 * A step already passed can be tapped to go back to it; one not reached yet
 * cannot, because the steps ahead depend on the ones behind — there is no
 * sense choosing a transporter before there is an address for it to reach.
 */
function Steps({ current, onGo, pickup }) {
  // A store pickup has no transporter to choose: its Transport step is not
  // shown, and the ones after it are numbered on from Delivery.
  const shown = STEP_NAMES.map((name, i) => ({ name, n: i + 1 })).filter(
    ({ n }) => !(pickup && n === 3),
  )
  return (
    <ol className="co-steps">
      {shown.map(({ name, n }, i) => {
        const done = n < current
        return (
          <li
            key={name}
            className={`co-step${n === current ? ' on' : ''}${done ? ' done' : ''}`}
          >
            <button
              type="button"
              disabled={n >= current}
              onClick={() => onGo(n)}
              aria-current={n === current ? 'step' : undefined}
            >
              <span className="co-step-n">{done ? '✓' : i + 1}</span>
              <span className="co-step-name">{name}</span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

/** The heading each step asks its question under. */
const STEP_HEADS = [
  'Check your products',
  'Delivery or store pickup?',
  'How should we send it?',
  'Review and confirm',
]

/** The line under the button, saying what this step is for. */
const STEP_HINTS = [
  'Check what we read from your message. Change a quantity, remove the product or add missing product.',
  'Tell us where to deliver this order. You can save more than one address.',
  'Choose a transporter, or leave it to us — the shop will arrange one and tell you.',
]

function Review({ token, order, busy, problem, edit, onBrowse, onConfirm }) {
  const [step, setStep] = useState(1)
  // What stopped them moving on. Cleared on every move, so it never outlives
  // the thing it was complaining about.
  const [stepError, setStepError] = useState('')
  // null when closed; {mode:'add'} or {mode:'replace', lineId} when open —
  // the same picker, because searching the catalog is the same act whether
  // the line exists yet or not.
  const [picking, setPicking] = useState(null)
  const [search, setSearch] = useState('')
  const [found, setFound] = useState([])

  // The lines, not just whether there are any: what holds the order up has
  // to be nameable, and the first of them is where the customer is sent.
  //
  // A line the message named without a quantity arrives at zero. The server
  // refuses to place such an order, so the button is stopped here rather than
  // letting the customer tap it and be told no after the round trip.
  const needsQuantityLines = order.lines.filter((l) => Number(l.quantity) <= 0)
  // Likewise a line short of its category's minimum: the server refuses it,
  // and the line itself already says what the minimum is.
  const belowMinimumLines = order.lines.filter((l) => l.below_minimum)
  const needsQuantity = needsQuantityLines.length > 0
  const belowMinimum = belowMinimumLines.length > 0

  // Nowhere to send it. `ship_to` is the server's own answer to "where does
  // this go" — the address chosen for the order, else the customer's
  // default — so this agrees with what /confirm will refuse.
  // Collected from the store: no address, transporter or date is needed.
  const pickup = order.fulfilment === 'pickup'
  const needsAddress = !pickup && !order.ship_to
  const [searching, setSearching] = useState(false)

  // Typing is not a search. Waiting a moment after the last keystroke keeps
  // one request per word rather than one per letter, on a connection that
  // may be a phone on mobile data at a site.
  useEffect(() => {
    const term = search.trim()
    if (term.length < 2) {
      setFound([])
      return undefined
    }
    setSearching(true)
    const timer = setTimeout(() => {
      orderLinkApi
        .searchProducts(token, term)
        .then(setFound)
        .catch(() => setFound([]))
        .finally(() => setSearching(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [search, token])

  /**
   * What stops this step being left, or '' when nothing does.
   *
   * The same conditions the Confirm button has always had, split across the
   * steps that can fix them — so a customer is told about a missing quantity
   * on the screen holding the box, not three screens later.
   *
   * Step 3 has none: the shop can arrange transport itself, and /confirm does
   * not ask for one.
   */
  const blocker = (n) => {
    if (n === 1) {
      if (order.lines.length === 0) return 'Please add at least one product to your order.'
      // Named, not counted. 'Some items are below their minimum order' is
      // read on a screen holding ten of them and points at none — and these
      // are the words /confirm itself answers with, so the page and the
      // server do not describe the same refusal two different ways.
      if (needsQuantity)
        return `Please enter how much you need of: ${names(needsQuantityLines)}.`
      if (belowMinimum)
        return `Below the minimum order: ${names(belowMinimumLines)}. Please increase the quantity.`
    }
    if (n === 2 && needsAddress)
      return 'Please add a shipping address so we know where to send your order.'
    return ''
  }

  /**
   * Mark the lines holding the order up, and go to the first of them,
   * instead of printing a banner about them.
   *
   * Each already says what is wrong with it, under the box that fixes it —
   * 'Minimum order is 25 kg', 'Please enter how much you need'. What it
   * could not say is that it was the one being complained about, because on
   * a ten-line order every line looks the same from the top of the page. So
   * the message stays where it is and the line it belongs to is marked.
   *
   * False when there is no such line, so the caller can fall back to saying
   * it in words: an order with nothing on it has nowhere to send anyone.
   */
  const showFirstProblem = () => {
    const bad = needsQuantityLines.concat(belowMinimumLines)
    if (bad.length === 0) return false
    setFlagged(bad.map((l) => l.id))
    document
      .getElementById(`co-line-${bad[0].id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return true
  }

  // Which line is mid-edit, if any. `busy` is still what the footer and the
  // step buttons read — but handing it to every line meant a change to one
  // of them re-rendered, and visibly flickered, all ten. Only the line being
  // saved should know an edit is in flight.
  const [savingLine, setSavingLine] = useState(null)

  // Lines Next refused to move past. Set when they tap it, cleared the
  // moment the order changes — fixing the quantity takes the mark off
  // without them having to tap Next again to find out it worked.
  const [flagged, setFlagged] = useState([])
  useEffect(() => setFlagged([]), [order])

  const editLine = useCallback(
    (id, run) => {
      setSavingLine(id)
      return edit(run).finally(() => setSavingLine(null))
    },
    [edit],
  )

  // One stable function per action instead of four fresh closures per line
  // per render: Line is memoised, and a new handler would fail its
  // comparator every time and undo the whole point. Each takes the line it
  // is acting on, so none of them has to close over one.
  const changeQuantity = useCallback(
    (line, quantity) =>
      editLine(line.id, () => orderLinkApi.updateLine(token, line.id, { quantity })),
    [editLine, token],
  )
  const changeUnit = useCallback(
    (line, unit) =>
      editLine(line.id, () => orderLinkApi.updateLine(token, line.id, { unit })),
    [editLine, token],
  )
  const removeLine = useCallback(
    (line) => editLine(line.id, () => orderLinkApi.removeLine(token, line.id)),
    [editLine, token],
  )

  // Opening always starts clean: without this, a picker opened after an
  // earlier search shows that search's results under an empty box.
  const openPicker = useCallback((next) => {
    setSearch('')
    setFound([])
    setPicking(next)
  }, [])

  const changeProduct = useCallback(
    (line) => openPicker({ mode: 'replace', lineId: line.id }),
    [openPicker],
  )

  const closePicker = () => {
    setPicking(null)
    setSearch('')
  }

  // One handler for both modes. Replacing keeps the quantity already given —
  // they are correcting which product it was, not how much of it.
  const choose = (entry) => {
    edit(() =>
      picking.mode === 'replace'
        ? orderLinkApi.updateLine(token, picking.lineId, {
            product_id: entry.id,
            unit: entry.unit,
          })
        : orderLinkApi.addLine(token, {
            product_id: entry.id,
            quantity: startingQuantity(entry),
            unit: entry.unit,
          }),
    )
    closePicker()
  }

  const go = (n) => {
    setStepError('')
    setStep(n)
    // The steps are tall enough to scroll; landing halfway down the next one
    // reads as nothing having happened.
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const next = () => {
    const stop = blocker(step)
    if (stop) {
      // A problem belonging to one line is shown on that line. Go to it
      // rather than printing a vaguer copy at the top of the page; only
      // fall back to words when there is no line to go to.
      setStepError(step === 1 && showFirstProblem() ? '' : stop)
      return
    }
    // Nothing to choose on Transport for a store pickup.
    go(pickup && step === 2 ? 4 : step + 1)
  }

  // Everything that would make /confirm refuse, wherever it lives. Checked
  // again on the last step because the order can change under them: a line
  // removed on step 1 after they have walked through to step 4 still has to
  // stop the button.
  const notReady = blocker(1) || blocker(2)

  return (
    <Shell
      /* On the logo's row, above the order rather than below it: a customer
         whose message matched nothing needs the shop's list before they need
         to read what we got wrong, and searching is no use to them — it needs
         a word they do not have. */
      /* Only on the step about products. On the later ones the catalog is
         not what they are being asked about, and leaving for it would lose
         the step they had reached. */
      action={
        step === 1 ? (
          <button
            type="button"
            className="co-browse"
            disabled={busy}
            onClick={onBrowse}
          >
            View All Products
          </button>
        ) : null
      }
    >
      {/* What they sent sits beside the title rather than under it: it is
          part of "which order is this", and it kept the items a row lower. */}
      <header className={`co-head${order.media?.length ? ' co-head-media' : ''}`}>
        <div className="co-head-text">
          <h1>{STEP_HEADS[step - 1]}</h1>
          <p className="co-sub">
            {order.customer_name} · {order.order_number}
          </p>
          {whenCreated(order.created_at) && (
            <p className="co-sub">Created {whenCreated(order.created_at)}</p>
          )}
        </div>
        {/* Only beside the items: it is what the items were read from, and
            it has nothing to say about an address or a transporter. */}
        {step === 1 && <SentMedia token={token} media={order.media} />}
      </header>

      <Steps current={step} onGo={go} pickup={pickup} />

      {problem && <p className="co-error">{problem}</p>}
      {stepError && <p className="co-error">{stepError}</p>}

      {/* ---- step 1: the items ------------------------------------------ */}

      {/* The whole message, above the items it produced. Anything missing
          from the list below is somewhere in here, and a line matched to the
          wrong product is caught by reading the two together. Quoted
          verbatim, so the customer recognises it as their own words rather
          than as our summary of them. */}
      {step === 1 && order.heard && (
        <section className="co-heard-all">
          <h2>Your message</h2>
          <p className="co-heard-text">{order.heard}</p>
          <p className="co-heard-hint">
            Please check the products below. If anything is missing, search for it
            and add it to your order.
          </p>
        </section>
      )}

      {step === 1 && (
      <section className="co-card">
        <div className="co-card-title">
          <span>Products</span>
          <span className="co-muted">{order.lines.length}</span>
        </div>

        {order.lines.length === 0 && (
          <p className="co-empty">
            {order.heard
              ? 'Nothing from your message matched our list. Search below for what you need.'
              : 'Every product has been removed. Add one, or message us to cancel.'}
          </p>
        )}

        {order.lines.map((line) => (
          /* The picker for a line sits directly under that line. At the foot
             of the list it opened most of a screen below the pencil that
             asked for it — on a ten-line order, below the fold — and a panel
             you cannot see is indistinguishable from a tap that did nothing. */
          <Fragment key={line.id}>
            <Line
              line={line}
              flagged={flagged.includes(line.id)}
              pending={savingLine === line.id}
              onQuantity={changeQuantity}
              onUnit={changeUnit}
              onRemove={removeLine}
              onChangeProduct={changeProduct}
            />
            {picking?.mode === 'replace' && picking.lineId === line.id && (
              <ProductPicker
                title="Edit product"
                busy={busy}
                search={search}
                onSearch={setSearch}
                found={found}
                searching={searching}
                onChoose={choose}
                onCancel={closePicker}
              />
            )}
          </Fragment>
        ))}

        {/* Adding has no line to sit under, so it stays at the foot of the
            list where the button is. The button itself only steps aside for
            its own panel — changing a line above should not take away the
            way to add one. */}
        {picking?.mode === 'add' ? (
          <ProductPicker
            title="Add a product"
            busy={busy}
            search={search}
            onSearch={setSearch}
            found={found}
            searching={searching}
            onChoose={choose}
            onCancel={closePicker}
          />
        ) : (
          <button
            type="button"
            className="co-add"
            disabled={busy}
            onClick={() => openPicker({ mode: 'add' })}
          >
            + Add a product
          </button>
        )}
      </section>
      )}

      {/* ---- step 2: where it goes --------------------------------------- */}
      {step === 2 && (
        <>
          <FulfilmentSection token={token} order={order} busy={busy} edit={edit} />
          {!pickup && <ShippingSection token={token} order={order} busy={busy} edit={edit} />}
          <DeliveryDateSection token={token} order={order} busy={busy} edit={edit} />
        </>
      )}

      {/* ---- step 3: how it gets there ----------------------------------- */}
      {step === 3 && !pickup && (
        <TransportSection token={token} order={order} busy={busy} edit={edit} />
      )}

      {/* ---- step 4: everything above, read-only, and the button --------- */}
      {step === 4 && (
        <>
          <section className="co-card">
            <div className="co-card-title">
              <span>Products</span>
              <button type="button" className="co-change" onClick={() => go(1)}>
                Change
              </button>
            </div>
            {order.lines.map((line) => {
              // The rate is per the unit the product is *priced* in, which is
              // not always the one it was ordered in. '5 cartons · ₹135.00'
              // reads as ₹675 when the line is ₹19,912.50 — so the weight
              // those cartons come to is shown, and the rate says /kg.
              const priced = line.base_unit || line.unit
              const converted =
                line.quantity_base != null &&
                line.base_unit &&
                line.unit !== line.base_unit
              return (
                <div className="co-sum-line" key={line.id}>
                  <div className="co-sum-name">
                    <span className="co-line-name">{line.product_name}</span>
                    {line.product_name_ta && (
                      <span className="co-name-ta">{line.product_name_ta}</span>
                    )}
                  </div>
                  <div className="co-sum-calc">
                    <span className="co-muted">
                      {Number(line.quantity)} {qtyUnit(line.quantity, line.unit)}
                      {converted &&
                        ` (${Number(line.quantity_base)} ${qtyUnit(
                          line.quantity_base,
                          line.base_unit,
                        )})`}
                      {line.rate != null &&
                        ` × ${money(line.rate)}/${qtyUnit(1, priced)}`}
                    </span>
                    <span className="co-money">{money(line.line_total)}</span>
                  </div>
                  {/* Only where the tax was added on top. A SKU priced
                      inclusive of GST was charged exactly its rate — the
                      amount above is already rate × quantity — so breaking
                      out a tax base of ₹66.95 against a quoted ₹79.00/kg
                      explains nothing and invites the question of where the
                      customer's rate went. Where it was added, this is what
                      makes the total reachable from the rate. */}
                  {!line.rate_includes_tax && Number(line.line_tax) > 0 && (
                    <div className="co-muted co-sum-tax">
                      {money(line.line_subtotal)} + GST
                      {line.tax_rate != null && ` ${Number(line.tax_rate)}%`}{' '}
                      {money(line.line_tax)}
                    </div>
                  )}
                </div>
              )
            })}
          </section>

          {pickup && (
            <section className="co-card">
              <div className="co-card-title">
                <span>Store pickup</span>
                <button type="button" className="co-change" onClick={() => go(2)}>
                  Change
                </button>
              </div>
              <p className="co-muted">You will collect this order from our store.</p>
              <div className="co-kv">
                <span>Pickup date</span>
                <strong>{order.requested_delivery_date || 'Not chosen'}</strong>
              </div>
            </section>
          )}

          {!pickup && (
            <>
              <section className="co-card">
                <div className="co-card-title">
                  <span>Shipping address</span>
                  <button type="button" className="co-change" onClick={() => go(2)}>
                    Change
                  </button>
                </div>
                {order.ship_to ? (
                  <p className="co-addr-text">{order.ship_to}</p>
                ) : (
                  <p className="co-empty">Not set yet.</p>
                )}
              </section>

              <section className="co-card">
                <div className="co-card-title">
                  <span>Transport service</span>
                  <button type="button" className="co-change" onClick={() => go(3)}>
                    Change
                  </button>
                </div>
                {order.transport ? (
                  <>
                    <p className="co-line-name">{order.transport.name}</p>
                    <p className="co-muted">
                      {order.transport.city}
                      {order.transport.contact_number &&
                        ` · ${order.transport.contact_number}`}
                    </p>
                  </>
                ) : (
                  <p className="co-muted">
                    Not chosen — we will arrange it and tell you how it is coming.
                  </p>
                )}
              </section>
            </>
          )}

          {!pickup && order.requested_delivery_date && (
            <section className="co-card">
              <div className="co-card-title">
                <span>Delivery</span>
              </div>
              <div className="co-kv">
                <span>Requested date</span>
                <strong>{order.requested_delivery_date}</strong>
              </div>
            </section>
          )}

          <section className="co-card">
            <div className="co-kv">
              <span>Value</span>
              <span className="co-money">{money(order.subtotal)}</span>
            </div>
            {/* One row per rate, each naming its percent: an order of 18% wire
                and 5% nails has no single rate to print. */}
            {gstByRate(order).map(({ rate, amount }) => (
              <div className="co-kv" key={rate}>
                <span>{rate ? `GST ${rate}` : 'GST'}</span>
                <span className="co-money">{money(amount)}</span>
              </div>
            ))}
            <div className="co-kv co-kv-total">
              <span>Total</span>
              <span className="co-money">{money(order.grand_total)}</span>
            </div>
          </section>
        </>
      )}

      {/* Pinned, because a step can run past a phone screen and the way on is
          the only thing the page is asking for. */}
      <div className="co-bar">
        <div>
          <div className="co-muted">Total</div>
          <div className="co-bar-total">{money(order.grand_total)}</div>
        </div>
        <div className="co-bar-actions">
          {step > 1 && (
            <button
              type="button"
              className="co-secondary"
              disabled={busy}
              onClick={() => go(pickup && step === 4 ? 2 : step - 1)}
            >
              Back
            </button>
          )}
          {step < 4 ? (
            <button type="button" className="co-primary" disabled={busy} onClick={next}>
              Next
            </button>
          ) : (
            <button
              type="button"
              className="co-primary"
              disabled={busy || !!notReady}
              onClick={onConfirm}
            >
              {busy ? 'Please wait…' : 'Confirm order'}
            </button>
          )}
        </div>
      </div>

      <p className="co-fineprint">
        {step === 4
          ? notReady ||
            'Confirming sends this order to our packing team. We will send a code on WhatsApp to make sure it is you.'
          : step === 2 && pickup
            ? 'Collect your order from our store. Choosing a pickup date is optional.'
            : STEP_HINTS[step - 1]}
      </p>
    </Shell>
  )
}

/**
 * The photo or voice note the order was read from, as the customer sent it.
 *
 * Shown so they can check our reading against their own message — the
 * list we photographed, the voice note we heard — without going back to
 * WhatsApp. Absent for an order that was typed or entered by the shop.
 */
function SentMedia({ token, media }) {
  const [viewing, setViewing] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [broken, setBroken] = useState({})
  if (!media?.length) return null

  const hasImage = media.includes('image') && !broken.image
  const hasAudio = media.includes('audio') && !broken.audio
  if (!hasImage && !hasAudio) return null

  return (
    <section className="co-media" aria-label="What you sent">
      <div className="co-media-buttons">
        <span className="co-media-label">What you sent</span>
        {hasImage && (
          <button type="button" className="co-media-btn" onClick={() => setViewing(true)}>
            <MediaIcon kind="image" />
            Photo
          </button>
        )}
        {hasAudio && (
          <button
            type="button"
            className={`co-media-btn${playing ? ' on' : ''}`}
            aria-expanded={playing}
            onClick={() => setPlaying((v) => !v)}
          >
            <MediaIcon kind="audio" />
            Voice note
          </button>
        )}
      </div>

      {/* The phone's own player: play, pause and seek are what customers
          already know, and it works the same on every browser. Started on
          the tap that opened it, which mobile browsers allow. */}
      {hasAudio && playing && (
        <audio
          className="co-media-audio"
          controls
          autoPlay
          preload="auto"
          src={orderLinkApi.mediaUrl(token, 'audio')}
          onError={() => {
            setBroken((b) => ({ ...b, audio: true }))
            setPlaying(false)
          }}
        />
      )}

      {hasImage && viewing && (
        <ImagePreview
          src={orderLinkApi.mediaUrl(token, 'image')}
          onClose={() => setViewing(false)}
          onError={() => {
            setBroken((b) => ({ ...b, image: true }))
            setViewing(false)
          }}
        />
      )}
    </section>
  )
}

/** Full-screen photo. Tap outside, the ×, or Escape closes it. */
function ImagePreview({ src, onClose, onError }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    // The page behind should not scroll while the photo is up.
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [onClose])

  return (
    <div
      className="co-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Your photo"
      onClick={onClose}
    >
      <button type="button" className="co-modal-close" aria-label="Close" onClick={onClose}>
        ×
      </button>
      {/* Clicks on the photo itself do not close it: on a phone a tap to
          look closer would otherwise dismiss what they were looking at. */}
      <img
        src={src}
        alt="The photo you sent"
        className="co-modal-img"
        onClick={(e) => e.stopPropagation()}
        onError={onError}
      />
      {/* Pinch-zoom inside a fixed overlay is unreliable across phones; the
          browser's own image view zooms everywhere. */}
      <a
        className="co-modal-open"
        href={src}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        Open full size
      </a>
    </div>
  )
}

/**
 * A searchable dropdown of transport services.
 *
 * A native <select> cannot be searched on a phone, and a city can list many
 * transporters. This opens in place — under the field, not floating over the
 * page — so the phone keyboard rising for the search box pushes the list up
 * with it instead of covering it.
 */
function TransportPicker({
  services,
  chosen,
  chosenId,
  busy,
  showCity,
  homeCity,
  onSearch,
  onChoose,
}) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  // Search results from every city, or null when not searching.
  const [results, setResults] = useState(null)
  const [searching, setSearching] = useState(false)
  const boxRef = useRef(null)

  // Tap outside or Escape closes it, as any dropdown does.
  useEffect(() => {
    if (!open) return undefined
    const away = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false)
    }
    const esc = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  // Name, number, address or city — searched across every city, not just
  // the one listed: the transporter that reaches their delivery address may
  // be registered in another town. A moment after the last keystroke, so
  // it is one request per word on a phone's data.
  const needle = term.trim()
  useEffect(() => {
    if (needle.length < 2) {
      setResults(null)
      setSearching(false)
      return undefined
    }
    setSearching(true)
    let cancelled = false
    const timer = setTimeout(() => {
      onSearch(needle)
        .then((rows) => !cancelled && setResults(rows))
        .catch(() => !cancelled && setResults([]))
        .finally(() => !cancelled && setSearching(false))
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [needle, onSearch])

  const isSearch = results !== null
  const shown = isSearch ? results : services
  // A search spans cities, so every result says which; so does a service in
  // the default list from another city (one they added or used before).
  const cityShown = showCity || isSearch
  const awayFrom = (s) =>
    Boolean(homeCity) && String(s.city ?? '').toLowerCase() !== homeCity.toLowerCase()

  const choose = (service) => {
    setOpen(false)
    setTerm('')
    if (service.id !== chosenId) onChoose(service.id)
  }

  return (
    <div className="co-tp" ref={boxRef}>
      <button
        type="button"
        className={`co-tp-field${open ? ' open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
      >
        {chosen ? (
          <span className="co-tp-value">
            <span className="co-line-name">{chosen.name}</span>
            <span className="co-muted">
              {transportNumbers(chosen).join(' / ')}
              {showCity ? ` · ${chosen.city}` : ''}
            </span>
          </span>
        ) : (
          <span className="co-tp-placeholder">Select Transport Service</span>
        )}
        <span className="co-tp-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {chosen && !chosen.is_approved && !open && (
        <p className="co-muted co-tp-note">New — the shop will check these details</p>
      )}

      {open && (
        <div className="co-tp-panel">
          <input
            type="search"
            autoFocus
            className="co-tp-search"
            placeholder="Search all cities by name, number or area"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            aria-label="Search transport services"
          />
          <ul className="co-tp-list" role="listbox" aria-label="Transport services">
            {shown.map((service) => {
              const on = service.id === chosenId
              return (
                <li key={service.id} role="option" aria-selected={on}>
                  <button
                    type="button"
                    className={`co-tp-option${on ? ' on' : ''}`}
                    onClick={() => choose(service)}
                  >
                    <span className="co-line-name">
                      {service.name}
                      {on && <span className="co-tp-tick"> ✓</span>}
                    </span>
                    {!service.is_approved && (
                      <span className="co-muted">New — the shop will check these details</span>
                    )}
                    <span className="co-muted">
                      {transportNumbers(service).join(' / ')}
                      {service.address ? ` · ${service.address}` : ''}
                      {cityShown || awayFrom(service) ? ` · ${service.city}` : ''}
                    </span>
                  </button>
                </li>
              )
            })}
            {searching && <li className="co-empty">Searching…</li>}
            {!searching && isSearch && shown.length === 0 && (
              <li className="co-empty">
                Nothing matches “{needle}” in any city. You can add it below.
              </li>
            )}
            {!isSearch && shown.length === 0 && (
              <li className="co-empty">
                None listed here yet — search other cities above, or add yours below.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Search the catalog: to swap a line's product, or to add one.
 *
 * Opens in place rather than over the page, for the reason the transport
 * picker does — it is mostly a search box, and on a phone the keyboard rises
 * over the lower half of the screen. A floating card is the thing that
 * keyboard covers; a panel in the flow gets pushed up with it.
 *
 * Which is why Remove is a dialog and this is not. Removing is one
 * irreversible yes or no that should interrupt; this is a task with typing
 * in it, and the line it is about is worth keeping on screen beside it.
 */
function ProductPicker({
  title,
  busy,
  search,
  onSearch,
  found,
  searching,
  onChoose,
  onCancel,
}) {
  const box = useRef(null)

  // The pencil that opened this can be most of a screen above the panel on a
  // long order. Brought into view, because a panel below the fold is
  // indistinguishable from a tap that did nothing.
  useEffect(() => {
    box.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])

  return (
    <div className="co-picker" ref={box}>
      {/* Title and the way out on one row. Cancel was a full-width block
          under the list, which cost a whole row of a panel that is mostly
          empty until they type. The heading echoes the control that opened
          it, so the panel reads as a continuation of the tap. */}
      <div className="co-picker-head">
        <span className="co-picker-title">{title}</span>
        <button type="button" className="co-picker-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <input
        autoFocus
        placeholder="Search the catalog"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
      />
      <div className="co-picker-list">
        {found.map((entry) => (
          <button
            type="button"
            key={entry.id}
            className="co-picker-row"
            disabled={busy}
            onClick={() => onChoose(entry)}
          >
            <span>
              <span className="co-line-name">{entry.name}</span>
              {entry.name_ta && <span className="co-name-ta">{entry.name_ta}</span>}
            </span>
            <span className="co-muted">
              {entry.rate == null
                ? ''
                : `${money(entry.rate)}/${
                    entry.unit === 'KILOGRAMS' ? 'kg' : unitLabel(entry.unit)
                  }`}
            </span>
          </button>
        ))}
        {searching && <p className="co-empty">Searching…</p>}
        {!searching && search.trim().length >= 2 && found.length === 0 && (
          <p className="co-empty">No matching items found.</p>
        )}
        {search.trim().length < 2 && (
          <p className="co-empty">Type a product name or size.</p>
        )}
      </div>
    </div>
  )
}

/**
 * 'Remove this product?' — over the page, not inside the line.
 *
 * As a strip under the line it was easy to miss: on a long order the line
 * being removed can sit near the bottom of the screen, so the question
 * opened below the fold and the tap read as having done nothing. Over the
 * page it is the only thing there is to answer.
 *
 * The product is named in it. 'Remove this product?' asked in the middle of
 * the screen has lost the line it came from, and the whole point of asking
 * is to catch the bin that was tapped by mistake.
 */
function ConfirmRemove({ name, busy, onKeep, onRemove }) {
  const keepRef = useRef(null)
  // Held in a ref so the effect below can run once. The parent builds these
  // callbacks inline, so depending on them would re-run it every render and
  // drag the focus back to Keep each time.
  const keep = useRef(onKeep)
  keep.current = onKeep

  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && keep.current()
    document.addEventListener('keydown', esc)
    // The page behind must not scroll: one that does reads as dismissible,
    // and on a phone the thumb that tapped the bin is still resting on it.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // Focus the safe one. Whichever way it is dismissed — Escape, the
    // backdrop, Enter on the focused button — nothing is lost.
    keepRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', esc)
      document.body.style.overflow = previous
    }
  }, [])

  return (
    <div
      className="co-confirm-backdrop"
      role="presentation"
      /* The backdrop itself only. A press that began on the card and drifted
         off it — selecting the product name, say — must not remove the line. */
      onClick={(e) => e.target === e.currentTarget && onKeep()}
    >
      <div
        className="co-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-label="Remove this product?"
      >
        <h2 className="co-confirm-title">Remove this product?</h2>
        {/* Short words, short sentences. The page is read on a phone by
            people whose first language is Tamil, so 'taken off' — a phrasal
            verb that means nothing from its parts — gives way to 'removed',
            and the pointer back to View All Products goes: naming a button
            they cannot see from here is one clause too many for a yes or no. */}
        <p className="co-confirm-message">
          <strong>{name}</strong> will be removed from your order. You can add
          it again later.
        </p>
        <div className="co-confirm-actions">
          <button ref={keepRef} type="button" className="co-secondary" onClick={onKeep}>
            Keep
          </button>
          <button type="button" className="co-danger" disabled={busy} onClick={onRemove}>
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}

/* Sized and weighted to the bin it sits beside, so the pair reads as one
   control rather than two buttons that happened to land together. */
function PencilIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
      <path d="M14.5 5.5l4 4" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16" />
      <path d="M9 7V4.5h6V7" />
      <path d="M6 7l1 13h10l1-13" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  )
}

function TruckIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 6h12v10H2z" />
      <path d="M14 10h4l3 3v3h-7" />
      <circle cx="6.5" cy="17.5" r="1.8" />
      <circle cx="17.5" cy="17.5" r="1.8" />
    </svg>
  )
}

function StoreIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l1.5-5h15L21 9" />
      <path d="M3 9h18v1.5a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0z" />
      <path d="M5 13v7h14v-7" />
      <path d="M10 20v-4h4v4" />
    </svg>
  )
}

function MediaIcon({ kind }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {kind === 'image' ? (
        <>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.6" />
          <path d="M21 16l-5-5-8 8" />
        </>
      ) : (
        <>
          <rect x="9" y="3" width="6" height="11" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
        </>
      )}
    </svg>
  )
}

/**
 * Which transporter the order goes out with.
 *
 * The list is the shop's, for the customer's own city. When theirs is not
 * on it they add it here, and it is chosen at once — they came to pick one,
 * not to fill in a form and then pick it.
 *
 * Choosing is optional: a customer collecting from the shop needs none.
 */
const BLANK_TRANSPORT = {
  name: '',
  contact_number: '',
  contact_number_2: '',
  contact_number_3: '',
  address: '',
  city: '',
  state: '',
}

/**
 * What a new line starts at: the category's minimum order where there is
 * one (in the product's own unit, which is what a new line is added in),
 * else 1 as before. Only the starting point — the customer can change it.
 */
function startingQuantity(product) {
  const minimum = Number(product?.min_quantity)
  return Number.isFinite(minimum) && minimum > 0 ? minimum : 1
}

/**
 * A transporter's numbers — the main one first, then the two extras — with
 * blanks dropped and a number given twice shown once.
 */
function transportNumbers(service) {
  const seen = new Set()
  return [service?.contact_number, service?.contact_number_2, service?.contact_number_3]
    .map((n) => (n ?? '').trim())
    .filter((n) => {
      const digits = n.replace(/\D/g, '')
      if (!digits || seen.has(digits)) return false
      seen.add(digits)
      return true
    })
}

const BLANK_ADDRESS = {
  label: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  contact_name: '',
  contact_phone: '',
}

/**
 * Where this order goes: one of the customer's saved shipping addresses —
 * the default ticked to begin with — or a new one they add here, which is
 * saved to their account for next time and chosen for this order.
 *
 * Required. It sits above the transporter because the address is what the
 * transporter has to reach, and an order with nowhere to go cannot be
 * packed — /confirm refuses one, so the button here refuses it first.
 */
/**
 * The date they would like it by — optional. Saved as soon as it is picked,
 * as the address is; the shop sees it on the order and prints it on the
 * order sheet. Today or later only.
 */
/**
 * Sent to an address, or collected from the store. A pickup needs no
 * address or transporter, so choosing it takes those off the page and the
 * Transport step out of the way.
 */
function FulfilmentSection({ token, order, busy, edit }) {
  const current = order.fulfilment === 'pickup' ? 'pickup' : 'delivery'
  const options = [
    ['delivery', 'Delivery to my address', 'We will deliver your order to your address by transport.', TruckIcon],
    ['pickup', 'Store pickup', 'You can collect your order directly from our store.', StoreIcon],
  ]
  return (
    <section className="co-card">
      <div className="co-card-title">
        <span>How will you get it?</span>
      </div>
      <div className="co-addr-list" role="radiogroup" aria-label="Delivery or store pickup">
        {options.map(([value, name, hint, Icon]) => {
          const on = value === current
          return (
            <button
              type="button"
              key={value}
              role="radio"
              aria-checked={on}
              className={`co-addr co-fulfil${on ? ' on' : ''}`}
              disabled={busy}
              onClick={() => !on && edit(() => orderLinkApi.setFulfilment(token, value))}
            >
              <span className="co-transport-icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="co-fulfil-body">
                <span className="co-line-name">
                  {name}
                  {on && <span className="co-tp-tick"> ✓</span>}
                </span>
                <span className="co-addr-text">{hint}</span>
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}

function DeliveryDateSection({ token, order, busy, edit }) {
  // Today in the customer's own calendar, as YYYY-MM-DD, for the picker's floor.
  const now = new Date()
  const today = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10)
  const value = order.delivery_date ?? ''
  // The same date either way: for a store pickup it is the day they come.
  const name = order.fulfilment === 'pickup' ? 'Pickup date' : 'Delivery date'

  return (
    <section className="co-card">
      <div className="co-card-title">
        <span>
          {name}
          {value && <span className="co-chosen-tag">Chosen</span>}
        </span>
      </div>
      <p className="co-muted">
        {order.fulfilment === 'pickup'
          ? 'Optional — which day will you come to collect it?'
          : 'Optional — when would you like it by?'}
      </p>
      <div className="co-date-row">
        <input
          type="date"
          className="co-date"
          value={value}
          min={today}
          disabled={busy}
          aria-label={name}
          onChange={(e) => {
            const picked = e.target.value
            if (picked !== value) {
              edit(() => orderLinkApi.setDeliveryDate(token, picked || null))
            }
          }}
        />
        {value && (
          <button
            type="button"
            className="co-link"
            disabled={busy}
            onClick={() => edit(() => orderLinkApi.setDeliveryDate(token, null))}
          >
            Clear
          </button>
        )}
      </div>
    </section>
  )
}

function ShippingSection({ token, order, busy, edit }) {
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState(BLANK_ADDRESS)
  const [errors, setErrors] = useState({})
  const [cities, setCities] = useState(null)

  // Editing a field takes its complaint away. Leaving it there while they
  // type the fix reads as the fix not working.
  const change = (field) => (e) => {
    const { value } = e.target
    setDraft((d) => ({ ...d, [field]: value }))
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev))
  }
  const book = order.shipping_addresses ?? []

  // The city list, for the add form — fetched the first time it opens.
  useEffect(() => {
    if (!adding || cities) return
    orderLinkApi
      .cities(token)
      .then((rows) => {
        setCities(rows)
        // The record can carry a city with no state against it. The state
        // picker now drives the city list, so without this the pre-filled
        // city would sit under 'Choose a state first' and be cleared by the
        // first thing they touched.
        setDraft((d) =>
          d.city && !d.state
            ? { ...d, state: rows.find((c) => c.name === d.city)?.state ?? '' }
            : d,
        )
      })
      .catch(() => setCities([]))
  }, [adding, cities, token])

  const byState = new Map()
  for (const c of cities ?? []) {
    if (!byState.has(c.state)) byState.set(c.state, [])
    byState.get(c.state).push(c.name)
  }
  const listed = (cities ?? []).some(
    (c) => c.name.toLowerCase() === (draft.city || '').toLowerCase(),
  )

  const openForm = () => {
    setErrors({})
    setDraft({
      ...BLANK_ADDRESS,
      city: order.customer_city ?? '',
      state: order.customer_state ?? '',
    })
    setAdding(true)
  }

  // Every complaint at once, each against the field it is about, rather than
  // the first one at the foot of the form — a customer who gets three wrong
  // should not have to submit three times to find that out.
  const submit = async (e) => {
    e.preventDefault()
    const found = {}
    // 'choose' or 'enter' depending on which the form is showing: a dropdown
    // when the shop keeps a city list, typed boxes when it does not.
    const how = cities && cities.length === 0 ? 'enter' : 'choose'
    if (!draft.address.trim()) found.address = 'Please enter the address.'
    if (!draft.state.trim()) found.state = `Please ${how} the state.`
    if (!draft.city.trim()) found.city = `Please ${how} the city.`
    const pin = draft.pincode.replace(/\D/g, '')
    if (pin && pin.length !== 6) found.pincode = 'Please enter a 6-digit pincode.'
    const phone = draft.contact_phone.replace(/\D/g, '')
    if (phone && (phone.length < 10 || phone.length > 15))
      found.contact_phone = 'Please enter 10 to 15 digits.'
    setErrors(found)
    if (Object.keys(found).length) return

    const ok = await edit(() =>
      orderLinkApi.addShippingAddress(token, {
        label: draft.label || null,
        address: draft.address,
        city: draft.city || null,
        state: draft.state || null,
        pincode: draft.pincode || null,
        contact_name: draft.contact_name || null,
        contact_phone: draft.contact_phone || null,
      }),
    )
    if (ok) {
      setAdding(false)
      setDraft(BLANK_ADDRESS)
    }
  }

  return (
    <section className="co-card">
      <div className="co-card-title">
        <span>
          Shipping address
          {order.shipping_address_id && <span className="co-chosen-tag">Chosen</span>}
        </span>
        {/* Required, and said so before they reach the bottom and find the
            button refusing them. Only while there is nothing to ship to —
            a chosen address or the customer's default both answer it. */}
        {!order.ship_to && <span className="co-required">Required</span>}
      </div>

      {book.length === 0 && !adding && (
        <p className="co-muted">
          No shipping address saved yet. Add one so we know where to send it.
        </p>
      )}

      {book.length > 0 && (
        <div className="co-addr-list" role="radiogroup" aria-label="Shipping address">
          {book.map((a) => {
            const on = a.id === order.shipping_address_id
            return (
              <button
                type="button"
                key={a.id}
                role="radio"
                aria-checked={on}
                className={`co-addr${on ? ' on' : ''}`}
                disabled={busy}
                onClick={() => !on && edit(() => orderLinkApi.chooseShippingAddress(token, a.id))}
              >
                <span className="co-line-name">
                  {a.label || 'Address'}
                  {a.is_default && <span className="co-muted"> · default</span>}
                  {on && <span className="co-tp-tick"> ✓</span>}
                </span>
                <span className="co-addr-text">{a.text}</span>
              </button>
            )
          })}
        </div>
      )}

      {!adding ? (
        <button type="button" className="co-add" disabled={busy} onClick={openForm}>
          + Add new address
        </button>
      ) : (
        <form className="co-transport-form" onSubmit={submit} noValidate>
          <div className="co-picker-title">Add new shipping address</div>
          <label>
            <span>
              Address <Req />
            </span>
            <textarea
              autoFocus
              rows={3}
              maxLength={1000}
              className={errors.address ? 'co-invalid' : undefined}
              aria-invalid={!!errors.address}
              value={draft.address}
              onChange={change('address')}
              placeholder="Door no, street, area"
            />
            <FieldError message={errors.address} />
          </label>
          {cities && cities.length === 0 ? (
            /* Nothing managed to choose from, so both are typed. */
            <>
              <label>
                <span>
                  State <Req />
                </span>
                <input
                  value={draft.state}
                  maxLength={80}
                  className={errors.state ? 'co-invalid' : undefined}
                  aria-invalid={!!errors.state}
                  onChange={change('state')}
                />
                <FieldError message={errors.state} />
              </label>
              <label>
                <span>
                  City <Req />
                </span>
                <input
                  value={draft.city}
                  maxLength={80}
                  className={errors.city ? 'co-invalid' : undefined}
                  aria-invalid={!!errors.city}
                  onChange={change('city')}
                />
                <FieldError message={errors.city} />
              </label>
            </>
          ) : (
            /* State chosen first, city within it — rather than one list of
               every city grouped by state, with the state read back out of
               whatever was picked. The customer knows their state without
               thinking, and choosing it cuts the city list to the handful
               that could be right. */
            <>
              <label>
                <span>
                  State <Req />
                </span>
                <select
                  className={`co-select${errors.state ? ' co-invalid' : ''}`}
                  value={draft.state}
                  disabled={!cities}
                  aria-invalid={!!errors.state}
                  onChange={(e) => {
                    const state = e.target.value
                    // A city has to sit in the state chosen, so one that no
                    // longer does is cleared rather than left contradicting it.
                    const keeps = (byState.get(state) ?? []).includes(draft.city)
                    setDraft({ ...draft, state, city: keeps ? draft.city : '' })
                    setErrors((prev) =>
                      prev.state || prev.city ? { ...prev, state: '', city: '' } : prev,
                    )
                  }}
                >
                  {!cities && (
                    <option value={draft.state}>{draft.state || 'Loading…'}</option>
                  )}
                  {cities && <option value="">Choose a state</option>}
                  {/* A state already on their record that the managed list
                      does not carry still has to be selectable, or opening
                      the form would silently drop it. */}
                  {cities && draft.state && !byState.has(draft.state) && (
                    <option value={draft.state}>{draft.state}</option>
                  )}
                  {[...byState.keys()].sort().map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
                <FieldError message={errors.state} />
              </label>
              <label>
                <span>
                  City <Req />
                </span>
                <select
                  className={`co-select${errors.city ? ' co-invalid' : ''}`}
                  value={draft.city}
                  disabled={!cities || !draft.state}
                  aria-invalid={!!errors.city}
                  onChange={change('city')}
                >
                  {!cities && (
                    <option value={draft.city}>{draft.city || 'Loading…'}</option>
                  )}
                  {cities && (
                    <option value="">
                      {draft.state ? 'Choose a city' : 'Choose a state first'}
                    </option>
                  )}
                  {cities && draft.city && !listed && (
                    <option value={draft.city}>{draft.city}</option>
                  )}
                  {(byState.get(draft.state) ?? []).map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <FieldError message={errors.city} />
              </label>
            </>
          )}
          <label>
            <span>Pincode</span>
            <input
              inputMode="numeric"
              maxLength={7}
              className={errors.pincode ? 'co-invalid' : undefined}
              aria-invalid={!!errors.pincode}
              value={draft.pincode}
              onChange={change('pincode')}
              placeholder="625020"
            />
            <FieldError message={errors.pincode} />
          </label>
          <label>
            <span>Contact name</span>
            <input
              value={draft.contact_name}
              maxLength={120}
              onChange={change('contact_name')}
              placeholder="Who receives it"
            />
          </label>
          <label>
            <span>Contact phone</span>
            <input
              type="tel"
              inputMode="tel"
              className={errors.contact_phone ? 'co-invalid' : undefined}
              aria-invalid={!!errors.contact_phone}
              value={draft.contact_phone}
              onChange={change('contact_phone')}
              placeholder="98765 43210"
            />
            <FieldError message={errors.contact_phone} />
          </label>
          <button type="submit" className="co-primary co-block" disabled={busy}>
            {busy ? 'Please wait…' : 'Add and choose'}
          </button>
          <button
            type="button"
            className="co-link"
            onClick={() => {
              setAdding(false)
              setErrors({})
            }}
          >
            Cancel
          </button>
        </form>
      )}
    </section>
  )
}

function TransportSection({ token, order, busy, edit }) {
  const [services, setServices] = useState(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState(BLANK_TRANSPORT)
  const [errors, setErrors] = useState({})

  // Editing a field takes its complaint away. Leaving it there while they
  // type the fix reads as the fix not working.
  const change = (field) => (e) => {
    const { value } = e.target
    setDraft((d) => ({ ...d, [field]: value }))
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev))
  }
  // The city list, fetched the first time the add form opens.
  const [cities, setCities] = useState(null)
  const chosenId = order.transport?.id ?? null

  // Where the order is going, which is what the list is drawn from — the
  // carriers that reach that city, whether based there or running a route
  // through it. The server works this out from the chosen shipping address,
  // so the city is not sent: one place decides it.
  const listCity = order.ship_to_city ?? ''

  // Reloaded when the choice changes, so a service they just added appears,
  // and when the shipping address changes — a different destination is a
  // different set of carriers, and the old list would be for the old city.
  useEffect(() => {
    let cancelled = false
    setServices(null)
    orderLinkApi
      .transportServices(token, {})
      .then((rows) => !cancelled && setServices(rows))
      .catch(() => !cancelled && setServices([]))
    return () => {
      cancelled = true
    }
  }, [token, chosenId, listCity])

  // The city list, for the add form — fetched the first time it opens.
  useEffect(() => {
    if (!adding || cities) return
    orderLinkApi
      .cities(token)
      .then((rows) => {
        setCities(rows)
        // The record can carry a city with no state against it. The state
        // picker now drives the city list, so without this the pre-filled
        // city would sit under 'Choose a state first' and be cleared by the
        // first thing they touched.
        setDraft((d) =>
          d.city && !d.state
            ? { ...d, state: rows.find((c) => c.name === d.city)?.state ?? '' }
            : d,
        )
      })
      .catch(() => setCities([]))
  }, [adding, cities, token])

  const searchAll = useCallback((q) => orderLinkApi.transportServices(token, { q }), [token])

  // The add form starts on the customer's own city — Madurai for a Madurai
  // customer — so most never touch it.
  const openForm = () => {
    setErrors({})
    setDraft({
      ...BLANK_TRANSPORT,
      city: order.customer_city ?? '',
      state: order.customer_state ?? '',
    })
    setAdding(true)
  }

  // Cities grouped by state, with the customer's own kept even if it is not
  // in the managed list (typed on their record before the list existed).
  const byState = new Map()
  for (const c of cities ?? []) {
    if (!byState.has(c.state)) byState.set(c.state, [])
    byState.get(c.state).push(c.name)
  }
  const listed = (cities ?? []).some(
    (c) => c.name.toLowerCase() === (draft.city || '').toLowerCase(),
  )

  // Every complaint at once, each against the field it is about, rather than
  // the first one at the foot of the form — a customer who gets three wrong
  // should not have to submit three times to find that out.
  const submit = async (e) => {
    e.preventDefault()
    const found = {}
    if (!draft.name.trim()) found.name = 'Please enter the transport service name.'
    const digits = draft.contact_number.replace(/\D/g, '')
    if (digits.length < 10 || digits.length > 15)
      found.contact_number = 'Please enter 10 to 15 digits.'
    // The second and third numbers are optional; when given, the same rule.
    const second = draft.contact_number_2.replace(/\D/g, '')
    if (second && (second.length < 10 || second.length > 15))
      found.contact_number_2 = 'Please enter 10 to 15 digits.'
    const third = draft.contact_number_3.replace(/\D/g, '')
    if (third && (third.length < 10 || third.length > 15))
      found.contact_number_3 = 'Please enter 10 to 15 digits.'
    // 'choose' or 'enter' depending on which the form is showing: a dropdown
    // when the shop keeps a city list, typed boxes when it does not.
    const how = cities && cities.length === 0 ? 'enter' : 'choose'
    if (!draft.state.trim()) found.state = `Please ${how} the state.`
    if (!draft.city.trim()) found.city = `Please ${how} the city.`
    setErrors(found)
    if (Object.keys(found).length) return

    const ok = await edit(() =>
      orderLinkApi.addTransport(token, {
        name: draft.name,
        contact_number: draft.contact_number,
        contact_number_2: draft.contact_number_2 || null,
        contact_number_3: draft.contact_number_3 || null,
        address: draft.address || null,
        city: draft.city,
        state: draft.state || null,
      }),
    )
    if (ok) {
      setAdding(false)
      setDraft(BLANK_TRANSPORT)
    }
  }

  return (
    <section className="co-card co-transport-card">
      <div className="co-card-title co-transport-title">
        <span>
          Transport service
          {order.transport && <span className="co-chosen-tag">Chosen</span>}
        </span>
        <span className="co-transport-icon" aria-hidden="true">
          <TruckIcon />
        </span>
      </div>
      <p className="co-muted co-transport-hint">
        {listCity
          ? `Showing the services that reach ${listCity}, where this order is going — search to find one in any city.`
          : 'Choose how we should send your order.'}
      </p>

      {services === null ? (
        <p className="co-empty">Loading…</p>
      ) : (
        <TransportPicker
          services={services}
          chosen={order.transport}
          chosenId={chosenId}
          busy={busy}
          showCity={!listCity}
          homeCity={listCity}
          onSearch={searchAll}
          onChoose={(id) => edit(() => orderLinkApi.chooseTransport(token, id))}
        />
      )}

      {order.transport && !adding && (
        <button
          type="button"
          className="co-link"
          disabled={busy}
          onClick={() => edit(() => orderLinkApi.chooseTransport(token, null))}
        >
          I don&apos;t need a transport service
        </button>
      )}

      {!adding ? (
        <button
          type="button"
          className="co-add"
          disabled={busy}
          onClick={openForm}
        >
          + Add new transport service
        </button>
      ) : (
        <form className="co-transport-form" onSubmit={submit} noValidate>
          <div className="co-picker-title">Add new transport service</div>
          <label>
            <span>
              Name <Req />
            </span>
            <input
              autoFocus
              value={draft.name}
              maxLength={120}
              className={errors.name ? 'co-invalid' : undefined}
              aria-invalid={!!errors.name}
              onChange={change('name')}
              placeholder="e.g. KPN Parcel Service"
            />
            <FieldError message={errors.name} />
          </label>
          <label>
            <span>
              Contact number <Req />
            </span>
            <input
              type="tel"
              inputMode="tel"
              className={errors.contact_number ? 'co-invalid' : undefined}
              aria-invalid={!!errors.contact_number}
              value={draft.contact_number}
              onChange={change('contact_number')}
              placeholder="98765 43210"
            />
            <FieldError message={errors.contact_number} />
          </label>
          <label>
            <span>Contact number 2</span>
            <input
              type="tel"
              inputMode="tel"
              className={errors.contact_number_2 ? 'co-invalid' : undefined}
              aria-invalid={!!errors.contact_number_2}
              value={draft.contact_number_2}
              onChange={change('contact_number_2')}
              placeholder="Another number to try"
            />
            <FieldError message={errors.contact_number_2} />
          </label>
          <label>
            <span>Contact number 3</span>
            <input
              type="tel"
              inputMode="tel"
              className={errors.contact_number_3 ? 'co-invalid' : undefined}
              aria-invalid={!!errors.contact_number_3}
              value={draft.contact_number_3}
              onChange={change('contact_number_3')}
              placeholder="Another number to try"
            />
            <FieldError message={errors.contact_number_3} />
          </label>
          <label>
            <span>Address</span>
            <textarea
              rows={2}
              maxLength={500}
              value={draft.address}
              onChange={change('address')}
              placeholder="Office or booking point"
            />
          </label>
          {/* A list rather than free text, so the service is filed under the
              same spelling of the city customers are matched on. Falls back
              to typing when the shop has not set up a city list. */}
          {cities && cities.length === 0 ? (
            /* Nothing managed to choose from, so both are typed. */
            <>
              <label>
                <span>
                  State <Req />
                </span>
                <input
                  value={draft.state}
                  maxLength={80}
                  className={errors.state ? 'co-invalid' : undefined}
                  aria-invalid={!!errors.state}
                  onChange={change('state')}
                />
                <FieldError message={errors.state} />
              </label>
              <label>
                <span>
                  City <Req />
                </span>
                <input
                  value={draft.city}
                  maxLength={80}
                  className={errors.city ? 'co-invalid' : undefined}
                  aria-invalid={!!errors.city}
                  onChange={change('city')}
                />
                <FieldError message={errors.city} />
              </label>
            </>
          ) : (
            /* State chosen first, city within it — rather than one list of
               every city grouped by state, with the state read back out of
               whatever was picked. The customer knows their state without
               thinking, and choosing it cuts the city list to the handful
               that could be right. */
            <>
              <label>
                <span>
                  State <Req />
                </span>
                <select
                  className={`co-select${errors.state ? ' co-invalid' : ''}`}
                  value={draft.state}
                  disabled={!cities}
                  aria-invalid={!!errors.state}
                  onChange={(e) => {
                    const state = e.target.value
                    // A city has to sit in the state chosen, so one that no
                    // longer does is cleared rather than left contradicting it.
                    const keeps = (byState.get(state) ?? []).includes(draft.city)
                    setDraft({ ...draft, state, city: keeps ? draft.city : '' })
                    setErrors((prev) =>
                      prev.state || prev.city ? { ...prev, state: '', city: '' } : prev,
                    )
                  }}
                >
                  {!cities && (
                    <option value={draft.state}>{draft.state || 'Loading…'}</option>
                  )}
                  {cities && <option value="">Choose a state</option>}
                  {/* A state already on their record that the managed list
                      does not carry still has to be selectable, or opening
                      the form would silently drop it. */}
                  {cities && draft.state && !byState.has(draft.state) && (
                    <option value={draft.state}>{draft.state}</option>
                  )}
                  {[...byState.keys()].sort().map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
                <FieldError message={errors.state} />
              </label>
              <label>
                <span>
                  City <Req />
                </span>
                <select
                  className={`co-select${errors.city ? ' co-invalid' : ''}`}
                  value={draft.city}
                  disabled={!cities || !draft.state}
                  aria-invalid={!!errors.city}
                  onChange={change('city')}
                >
                  {!cities && (
                    <option value={draft.city}>{draft.city || 'Loading…'}</option>
                  )}
                  {cities && (
                    <option value="">
                      {draft.state ? 'Choose a city' : 'Choose a state first'}
                    </option>
                  )}
                  {cities && draft.city && !listed && (
                    <option value={draft.city}>{draft.city}</option>
                  )}
                  {(byState.get(draft.state) ?? []).map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <FieldError message={errors.city} />
              </label>
            </>
          )}
          <button type="submit" className="co-primary co-block" disabled={busy}>
            {busy ? 'Please wait…' : 'Add and choose'}
          </button>
          <button
            type="button"
            className="co-link"
            onClick={() => {
              setAdding(false)
              setErrors({})
            }}
          >
            Cancel
          </button>
        </form>
      )}
    </section>
  )
}

/**
 * One line of the order, memoised.
 *
 * Every edit replaces the whole order, so each line arrives as a brand-new
 * object and a plain component would re-render all ten for a change to one
 * — the flicker that put this here. The comparator below reads the fields
 * this line actually draws; the handlers are compared by identity, which is
 * why the parent holds them in useCallback rather than building a closure
 * per line per render.
 */
const Line = memo(
  function Line({ line, flagged, pending, onQuantity, onUnit, onRemove, onChangeProduct }) {
    const [confirmingRemove, setConfirmingRemove] = useState(false)
    // Typed while the server is still catching up: the box holds what they
    // typed, and the order's own figure takes over once the edit lands.
    const [draft, setDraft] = useState(null)
    // '2', not '2.000': the API sends three decimals, and trailing zeros read
    // as precision nobody asked for. A real fraction ('2.5') is kept.
    const shown = draft ?? String(Number(line.quantity))
    // + and − move by the minimum — 25, 50, 75 kg — where there is one; a
    // typed amount is rounded up to the next of those by the server on save.
    const stepBy = Number(line.quantity_step) || 1

    // Short of the minimum as typed, not only as saved: the box can be read
    // against the minimum before the edit reaches the server. Nothing is
    // changed — the customer's number stays in the box.
    const typed = Number(shown)
    // While they are typing, and only then — saved, the server's verdict
    // stands. `minimum_qty` is already in this line's unit, so the two
    // numbers compare directly. It used to be worked out here, by dividing
    // the stored quantity_base by the stored quantity for a unit weight;
    // that pair goes stale the moment a weight is edited, and when a carton
    // moved from 25 kg the sum disagreed with the server about a saved line
    // — which refused it while this printed nothing.
    const belowMinimum =
      draft != null && line.minimum_qty != null && Number.isFinite(typed) && typed > 0
        ? typed < Number(line.minimum_qty)
        : Boolean(line.below_minimum)

    // Sold in multiples of the minimum: a typed amount between them is raised
    // to the next one when it is saved (26 kg -> 50 kg where it is 25). Said
    // while typing, so the price that comes back is not a surprise.
    const offStep = stepBy > 1 && Number.isFinite(typed) && typed > 0 && !isMultiple(typed, stepBy)
    const raisedTo = offStep ? roundUp(typed, stepBy) : null
    const unitShort = qtyUnit(2, line.unit)

    const commit = (value) => {
      const next = Number(value)
      if (!Number.isFinite(next) || next <= 0) {
        setDraft(null)                        // nonsense; snap back
        return
      }
      if (Number(next) === Number(line.quantity)) {
        setDraft(null)
        return
      }
      setDraft(null)
      onQuantity(line, String(next))
    }

    // No marker for a line we guessed at. The customer is reading every line
    // anyway, and 'You said: …' below already shows them the words it came
    // from — a warning stripe beside it only makes their own order look broken.
    // The id is how Next brings the line holding the order up into view,
    // rather than saying so in a banner the line is scrolled away from.
    return (
      <article
        className={`co-line${flagged ? ' co-line-flagged' : ''}`}
        id={`co-line-${line.id}`}
      >
        <div className="co-line-head">
          <div>
            {/* Always set: the API keeps lines it could not match to a product
                out of this list entirely and prints them above as a note, so
                there is no nameless line left to caption. */}
            <div className="co-line-name">{line.product_name}</div>
            {/* Both names, whichever language they chose. The person reading
                the page is often not the person taking delivery, and an
                English name beside a Tamil one is what makes the two agree at
                the counter. Absent until the catalog is translated, so the
                English name has to stand on its own. */}
            {line.product_name_ta && (
              <div className="co-name-ta">{line.product_name_ta}</div>
            )}
            {/* What they actually said, so a wrong match is obvious at a
                glance rather than only after the delivery arrives. */}
            {line.uncertain && (
              <div className="co-muted co-heard">You said: “{line.heard_as}”</div>
            )}
            {/* The message said which product but never how much, so the line
                came through at zero. Said plainly, because the customer is the
                only one who knows the number and the order cannot be placed
                until they give it. */}
            {Number(line.quantity) <= 0 && (
              <div className="co-needs-qty">Please enter how much you need</div>
            )}
          </div>
          {/* The two things that can be done to the whole line, together in the
              corner: swap it for another product, or take it off the order.
              Icons rather than a labelled button, because the name beside them
              is what they are about and a word for each only crowds it. Green
              against the bin's red — one changes the line, the other ends it,
              and they must never be tapped for each other. */}
          <div className="co-line-acts">
            <button
              type="button"
              className="co-edit"
              aria-label={`Change ${line.product_name}`}
              title="Edit product"
              disabled={pending}
              onClick={() => onChangeProduct(line)}
            >
              <PencilIcon />
            </button>
            <button
              type="button"
              className="co-remove"
              aria-label={`Remove ${line.product_name}`}
              title="Remove"
              disabled={pending}
              onClick={() => setConfirmingRemove(true)}
            >
              <TrashIcon />
            </button>
          </div>
        </div>

        <div className="co-line-body">
          <div className="co-stepper">
            {/* Stops at the last whole unit rather than walking to zero. A
                quantity of nothing is not an order for nothing, it is an
                unanswered question — and the bin beside the name is how a
                line is removed, which is unambiguous. */}
            <button
              type="button"
              aria-label="Less"
              disabled={pending || Number(line.quantity) - stepBy <= 0}
              onClick={() => commit(Number((Number(line.quantity) - stepBy).toFixed(3)))}
            >
              −
            </button>
            <input
              inputMode="decimal"
              value={shown}
              aria-label={`Quantity of ${line.product_name ?? 'this product'}`}
              onChange={(e) => {
                if (!/^\d*\.?\d{0,3}$/.test(e.target.value)) return
                setDraft(e.target.value)
              }}
              onBlur={(e) => commit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
              }}
            />
            <button
              type="button"
              aria-label="More"
              disabled={pending}
              onClick={() => commit(Number((Number(line.quantity) + stepBy).toFixed(3)))}
            >
              +
            </button>
          </div>
          {/* Beside the stepper, not inside it. Within it, the unit was a
              fourth item competing with three 44px targets for one row of a
              320px phone — 'Kilograms / கிலோ' wrapped mid-phrase and the
              dropdown was squeezed to a sliver. Its own element, so the
              narrow layout can give it a row of its own. */}
          {/* Only the units this product converts to — the server works out
              which those are, because it knows the SKU's conversion rate and
              what a bag weighs in this category. A unit that cannot convert
              would price the line at zero. */}
          {line.unit_options?.length > 1 ? (
            <select
              className="co-unit-select"
              value={line.unit}
              disabled={pending}
              aria-label={`Unit for ${line.product_name ?? 'this product'}`}
              onChange={(e) => onUnit(line, e.target.value)}
            >
              {line.unit_options.map((option) => (
                <option key={option.code} value={option.code}>
                  {unitName(option)}
                  {option.is_base ? ' ·' : ''}
                </option>
              ))}
            </select>
          ) : (
            // One unit: the same name, in the same type, as the dropdown —
            // 'Kilograms / கிலோ', not a lower-case 'kilograms' beside it.
            <span className="co-unit">
              {unitName(line.unit_options?.find((o) => o.code === line.unit)) ??
                unitLabel(line.unit)}
            </span>
          )}

          <div className="co-line-total">{money(line.line_total)}</div>
        </div>

        {/* Short of what the shop sells this in, said under the box that fixes
            it. The order will not be placed until it is raised — it is never
            raised for them. */}
        {belowMinimum ? (
          <div className="co-needs-qty co-min-note">
            Minimum order is {line.minimum}. Please increase the quantity.
          </div>
        ) : (
          // While typing only: once they leave the box it is saved, rounded.
          offStep &&
          draft != null && (
            <div className="co-step-note">
              Sold in multiples of {qty(stepBy)} {unitShort}. {qty(typed)} {unitShort} will be
              rounded up to {qty(raisedTo)} {unitShort}.
            </div>
          )
        )}

        {/* Removing is asked about first: one tap on a phone is too easy. */}
        {confirmingRemove && (
          <ConfirmRemove
            name={line.product_name}
            busy={pending}
            onKeep={() => setConfirmingRemove(false)}
            onRemove={() => {
              setConfirmingRemove(false)
              onRemove(line)
            }}
          />
        )}

        {/* An order in bags is priced by weight: the customer should see the
            kilos we will charge for, not be surprised by them on the bill. */}
        {line.quantity_base != null && line.base_unit && line.unit !== line.base_unit && (
          <div className="co-muted co-line-foot">
            {Number(line.quantity_base)} {unitLabel(line.base_unit)} at{' '}
            {money(line.rate)}/{line.base_unit === 'KILOGRAMS' ? 'kg' : 'unit'}
          </div>
        )}
      </article>
    )
  },
  /* The fields this line draws. `line` itself is a new object on every edit,
     so comparing it by reference would never match and memo would do
     nothing. Listed out rather than deep-compared: a line carries fields
     this component never reads, and re-rendering for those is the thing
     being fixed. */
  (before, after) =>
    before.pending === after.pending &&
    before.flagged === after.flagged &&
    before.line.id === after.line.id &&
    before.line.quantity === after.line.quantity &&
    before.line.quantity_base === after.line.quantity_base &&
    before.line.quantity_step === after.line.quantity_step &&
    before.line.unit === after.line.unit &&
    before.line.rate === after.line.rate &&
    before.line.line_total === after.line.line_total &&
    before.line.product_name === after.line.product_name &&
    before.line.product_name_ta === after.line.product_name_ta &&
    before.line.heard_as === after.line.heard_as &&
    before.line.uncertain === after.line.uncertain &&
    before.line.minimum === after.line.minimum &&
    before.line.minimum_base === after.line.minimum_base &&
    before.line.below_minimum === after.line.below_minimum &&
    before.line.base_unit === after.line.base_unit &&
    sameUnits(before.line.unit_options, after.line.unit_options) &&
    before.onQuantity === after.onQuantity &&
    before.onUnit === after.onUnit &&
    before.onRemove === after.onRemove &&
    before.onChangeProduct === after.onChangeProduct,
)

/** The code gate. A confirmed order is money, so the link alone is not enough. */
function OtpStep({ token, phone, total, onBack, onVerified, onDead }) {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [resent, setResent] = useState(false)
  const boxes = useRef([])

  const code = digits.join('')

  const put = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    const next = [...digits]
    next[index] = digit
    setDigits(next)
    setError('')
    if (digit && index < OTP_LENGTH - 1) boxes.current[index + 1]?.focus()
  }

  const onKeyDown = (index, event) => {
    // Backspace on an empty box steps back, which is what everyone expects
    // and what makes a six-box input bearable on a phone.
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      boxes.current[index - 1]?.focus()
    }
  }

  // One paste fills every box: codes arrive on the same phone, and people
  // copy the whole thing out of the WhatsApp message.
  const onPaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '')
    if (!pasted) return
    event.preventDefault()
    const next = Array(OTP_LENGTH).fill('')
    for (let i = 0; i < Math.min(pasted.length, OTP_LENGTH); i += 1) next[i] = pasted[i]
    setDigits(next)
    boxes.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus()
  }

  const verify = async (event) => {
    event.preventDefault()
    if (code.length < OTP_LENGTH) {
      setError('Enter all six digits.')
      return
    }
    setBusy(true)
    setError('')
    try {
      onVerified(await orderLinkApi.verify(token, code))
    } catch (err) {
      if (err?.response?.status === 404) onDead(errorMessage(err))
      else {
        setError(errorMessage(err))
        setDigits(Array(OTP_LENGTH).fill(''))
        boxes.current[0]?.focus()
      }
    } finally {
      setBusy(false)
    }
  }

  const sendAgain = async () => {
    setError('')
    setResent(false)
    try {
      await orderLinkApi.requestCode(token)
      setDigits(Array(OTP_LENGTH).fill(''))
      setResent(true)
      boxes.current[0]?.focus()
    } catch (err) {
      if (err?.response?.status === 404) onDead(errorMessage(err))
      else setError(errorMessage(err))
    }
  }

  return (
    <Shell>
      <header className="co-head">
        <h1>Enter the code</h1>
        <p className="co-sub">
          We sent a {OTP_LENGTH}-digit code on WhatsApp{phone ? ` to ${phone}` : ''}.
        </p>
      </header>

      <form className="co-card" onSubmit={verify}>
        <div className="co-otp" onPaste={onPaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                boxes.current[index] = el
              }}
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              value={digit}
              disabled={busy}
              autoFocus={index === 0}
              aria-label={`Digit ${index + 1}`}
              onChange={(e) => put(index, e.target.value)}
              onKeyDown={(e) => onKeyDown(index, e)}
            />
          ))}
        </div>

        {error && <p className="co-error">{error}</p>}
        {resent && !error && <p className="co-notice">A new code is on its way.</p>}

        <button type="submit" className="co-primary co-block" disabled={busy}>
          {busy ? 'Checking…' : `Confirm order · ${money(total)}`}
        </button>

        <button type="button" className="co-link" disabled={busy} onClick={onBack}>
          Back to the order
        </button>
        <p className="co-fineprint">
          Didn&apos;t get it?{' '}
          <button type="button" className="co-link" disabled={busy} onClick={sendAgain}>
            Send again
          </button>
        </p>
      </form>
    </Shell>
  )
}

/**
 * A delivered order, shown so the customer can check it against what came,
 * with one thing to do: confirm that every item arrived.
 *
 * Read-only — nothing about a delivered order is theirs to change. Until
 * they confirm, a reminder comes every day and no new order is taken from
 * them, so the button is the whole point of the page.
 */
function Delivered({ token, order, onOrder, onDead }) {
  // Collected from the store: 'dispatched' is the handover, not a lorry.
  const pickup = order.fulfilment === 'pickup'
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')
  // 'Some Items Not Delivered': the form, the lines ticked, and a note.
  const [reporting, setReporting] = useState(false)
  const [missing, setMissing] = useState({})
  const [note, setNote] = useState('')

  const report = async (e) => {
    e.preventDefault()
    setBusy(true)
    setProblem('')
    try {
      const itemIds = Object.keys(missing).filter((id) => missing[id]).map(Number)
      onOrder(await orderLinkApi.reportDeliveryIssue(token, { item_ids: itemIds, note: note || null }))
      setReporting(false)
      setMissing({})
      setNote('')
    } catch (error) {
      if (error?.response?.status === 404) onDead(errorMessage(error))
      else setProblem(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const confirm = async () => {
    setBusy(true)
    setProblem('')
    try {
      onOrder(await orderLinkApi.confirmDelivery(token))
    } catch (error) {
      if (error?.response?.status === 404) onDead(errorMessage(error))
      else setProblem(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  if (order.delivery_confirmed) {
    return (
      <Shell>
        <div className="co-done">
          <div className="co-tick" aria-hidden="true">
            ✓
          </div>
          <h1>Delivery confirmed</h1>
          <p className="co-sub">
            {order.order_number} · {money(order.grand_total)}
          </p>
          <p className="co-sub">Thank you. You can send us your next order on WhatsApp.</p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <header className="co-head">
        <h1>
          {pickup
            ? 'Your order has been handed over'
            : order.on_the_way
              ? 'Your order is on its way'
              : 'Your order has been delivered'}
        </h1>
        <p className="co-sub">
          {order.customer_name} · {order.order_number}
        </p>
        <p className="co-sub">
          {order.on_the_way
            ? pickup
              ? 'Please check every item below and tap the button.'
              : 'When it arrives, check every item below and tap the button.'
            : 'Please check that every item below arrived.'}
        </p>
      </header>

      {problem && <p className="co-error">{problem}</p>}

      <section className="co-card">
        <div className="co-card-title">
          <span>Products</span>
          <span className="co-muted">{order.lines.length}</span>
        </div>
        {order.lines.map((line) => (
          <div className="co-delivered-line" key={line.id}>
            <div>
              <div className="co-line-name">{line.product_name}</div>
              {line.product_name_ta && <div className="co-name-ta">{line.product_name_ta}</div>}
            </div>
            <div className="co-delivered-qty">
              {Number(line.quantity)} {unitLabel(line.unit)}
            </div>
          </div>
        ))}
        <div className="co-kv co-kv-total">
          <span>Total</span>
          <span className="co-money">{money(order.grand_total)}</span>
        </div>
      </section>

      {/* The same highlighted card, with the truck, as on the order page. */}
      {pickup && (
        <section className="co-card">
          <div className="co-card-title">
            <span>Store pickup</span>
          </div>
          <p className="co-muted">Collected from our store.</p>
        </section>
      )}

      {!pickup && order.ship_to && (
        <section className="co-card">
          <div className="co-card-title">
            <span>Delivering to</span>
          </div>
          <p className="co-addr-text">{order.ship_to}</p>
        </section>
      )}

      {!pickup && order.transport && (
        <section className="co-card co-transport-card">
          <div className="co-card-title co-transport-title">
            <span>Transport service</span>
            <span className="co-transport-icon" aria-hidden="true">
              <TruckIcon />
            </span>
          </div>
          <div className="co-line-name">{order.transport.name}</div>
          <div className="co-muted">
            {/* Each number its own link, so a tap rings that one. */}
            {transportNumbers(order.transport).map((number, index) => (
              <span key={number}>
                {index > 0 && ' / '}
                <a href={`tel:${number}`}>{number}</a>
              </span>
            ))}
            {order.transport.address ? ` · ${order.transport.address}` : ''}
          </div>
        </section>
      )}

      {/* Reported and not yet put right: say so, and keep both answers
          available — the confirm button is how they close it once the rest
          arrives. */}
      {order.delivery_issue_open && (
        <div className="co-issue-note" role="status">
          <strong className="co-issue-title">You reported items not delivered</strong>
          {whenCreated(order.delivery_issue_at) && <> · {whenCreated(order.delivery_issue_at)}</>}
          <div>
            Our team will contact you. Once everything has arrived, tap{' '}
            <strong>All Items Are Delivered</strong>.
          </div>
        </div>
      )}

      {!reporting ? (
        <>
          <button
            type="button"
            className="co-delivered-btn"
            disabled={busy}
            onClick={confirm}
          >
            {busy ? 'Please wait…' : '✓ All Items Are Delivered'}
          </button>
          <button
            type="button"
            className="co-issue-btn"
            disabled={busy}
            onClick={() => {
              setProblem('')
              setReporting(true)
            }}
          >
            Some Items Not Delivered
          </button>
        </>
      ) : (
        <form className="co-issue-form" onSubmit={report}>
          <div className="co-picker-title">Which items did not arrive?</div>
          <p className="co-muted co-issue-help">Tick any that are missing — or just tell us below.</p>
          {order.lines.map((line) => (
            <label key={line.id} className="co-issue-line">
              <input
                type="checkbox"
                checked={Boolean(missing[line.id])}
                onChange={(e) => setMissing((m) => ({ ...m, [line.id]: e.target.checked }))}
              />
              <span>
                <span className="co-line-name">{line.product_name}</span>
                <span className="co-muted">
                  {' '}
                  · {Number(line.quantity)} {unitLabel(line.unit)}
                </span>
              </span>
            </label>
          ))}
          <label className="co-issue-note-field">
            <span>Anything else? (optional)</span>
            <textarea
              rows={3}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. only 8 of 10 bags came"
            />
          </label>
          <button type="submit" className="co-primary co-block" disabled={busy}>
            {busy ? 'Please wait…' : 'Send report'}
          </button>
          <button type="button" className="co-link" onClick={() => setReporting(false)}>
            Cancel
          </button>
        </form>
      )}
    </Shell>
  )
}

function Done({ orderNumber, total }) {
  return (
    <Shell>
      <div className="co-done">
        <div className="co-tick" aria-hidden="true">
          ✓
        </div>
        {/* Placed, not confirmed. The customer has agreed to their order and
            the shop has not yet accepted it — that is what PENDING means, and
            it is two different people agreeing to two different things.
            Saying 'confirmed' here promised the shop's answer before anyone
            at the shop had given one. */}
        <h1>Order placed</h1>
        <p className="co-sub">
          {orderNumber} · {money(total)}
        </p>
        <p className="co-sub">
          We will check your order and message you on WhatsApp once confirmed.
        </p>
      </div>
    </Shell>
  )
}

/** Brand frame shared by the three steps, so the header never re-mounts. */
function Shell({ children, action }) {
  const { siteLogo } = useBranding()
  // The green frame, empty, until the branding is known: never the old logo.
  const logo = siteLogo ? (
    <img src={siteLogo} alt="MetroMart" className="co-logo" />
  ) : (
    <span className="co-logo logo-placeholder" aria-hidden="true" />
  )
  return (
    <div className="co-page">
      <div className="co-inner">
        {/* `action` puts one control on the logo's row — the way into the
            catalog, on the screen where the logo would otherwise sit alone
            above a heading. Without one the logo stays centred, as it is on
            every other screen here. */}
        {action ? (
          <div className="co-brandbar">
            {logo}
            {action}
          </div>
        ) : (
          logo
        )}
        {children}
      </div>
    </div>
  )
}
